import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { ArrowBack } from '@mui/icons-material'
import {
  Box,
  IconButton,
  LinearProgress,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material'
import { routes } from '@/router'
import { usePlatform } from '@/hooks'

type SessionScreenProps = {
  title: string
  subtitle?: ReactNode
  progress?: number
  onBack?: () => void
  canBack?: boolean
  children: ReactNode
  footer?: ReactNode
}

export function SessionScreen({
  title,
  subtitle,
  progress,
  onBack,
  canBack = true,
  children,
  footer,
}: SessionScreenProps) {
  const { isAndroid } = usePlatform()

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 480,
        mx: 'auto',
        px: 2.5,
        minHeight: '100dvh',
        pt: isAndroid ? 3 : 'env(safe-area-inset-top, 0px)',
        '@media (min-width:900px)': {
          justifyContent: 'flex-start',
          pt: '6vh',
          maxWidth: 440,
          minHeight: '100vh',
        },
      }}
    >
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.75,
          minHeight: 68,
          position: 'sticky',
          top: 0,
          bgcolor: 'background.default',
          zIndex: 5,
        }}
      >
        {canBack ? (
          <IconButton
            aria-label="Voltar"
            onClick={onBack}
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: 'primary.50',
            }}
          >
            <ArrowBack />
          </IconButton>
        ) : (
          <Box sx={{ width: 40, flex: 'none' }} />
        )}
        {progress == null ? (
          <Box sx={{ flex: 1 }} />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              aria-label="Progresso do cadastro"
              sx={{
                width: '100%',
                maxWidth: 180,
                height: 4,
                borderRadius: 1,
                bgcolor: 'divider',
              }}
            />
          </Box>
        )}
        <Box sx={{ width: 40, flex: 'none' }} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          pt: 1.5,
          pb: 3,
          flex: { xs: 1, md: 'none' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography
              color="text.secondary"
              sx={{ '& b': { color: 'text.primary', fontWeight: 500 } }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {children}
      </Box>

      {footer && (
        <Box
          component="footer"
          sx={{
            position: { xs: 'sticky', md: 'static' },
            bottom: 0,
            bgcolor: 'background.default',
            pt: 1.5,
            pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            zIndex: 4,
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  )
}

export function SessionField({
  label,
  errorText,
  ...props
}: TextFieldProps & { label?: string; errorText?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {label && (
        <Typography
          component="label"
          htmlFor={props.id}
          variant="body2"
          fontWeight={500}
        >
          {label}
        </Typography>
      )}
      <TextField
        hiddenLabel
        error={Boolean(errorText)}
        helperText={errorText}
        {...props}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: 'primary.50',
            borderRadius: 1.5,
            minHeight: 52,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
            {
              borderColor: 'text.secondary',
            },
          '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
            {
              borderColor: 'error.main',
            },
          '& .MuiInputBase-input': {
            py: 1.75,
          },
        }}
      />
    </Box>
  )
}

export function SessionOption({
  selected,
  onClick,
  children,
}: {
  selected: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        minHeight: 56,
        px: 2,
        py: 1.5,
        width: '100%',
        textAlign: 'left',
        borderRadius: 1.5,
        border: '1.5px solid',
        borderColor: selected ? 'primary.main' : 'transparent',
        bgcolor: selected ? 'background.paper' : 'primary.50',
        color: 'text.primary',
        fontWeight: 500,
        font: 'inherit',
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          border: '2px solid',
          borderColor: selected ? 'primary.main' : 'divider',
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
          bgcolor: 'background.paper',
        }}
      >
        {selected && (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: 'primary.main',
            }}
          />
        )}
      </Box>
      {children}
    </Box>
  )
}

export function SessionLegal({ color = 'text.secondary' }: { color?: string }) {
  return (
    <Typography variant="caption" color={color} textAlign="center">
      Ao seguir, você está de acordo com os{' '}
      <Box
        component="a"
        href="https://app.alume.com/use-terms"
        target="_blank"
        rel="noreferrer"
        sx={{ color: 'inherit', fontWeight: 500 }}
      >
        Termos de uso
      </Box>{' '}
      e{' '}
      <Box
        component="a"
        href="https://app.alume.com/privacy-policy"
        target="_blank"
        rel="noreferrer"
        sx={{ color: 'inherit', fontWeight: 500 }}
      >
        Aviso de Privacidade
      </Box>
    </Typography>
  )
}

export function RedirectIfAuthenticated({
  children,
  to = routes.home,
}: {
  children: ReactNode
  to?: string
}) {
  if (localStorage.getItem('access_token')) {
    return <Navigate to={to} replace />
  }

  return children
}
